import classNames from "classnames";
import { ReactNode } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../utils/constants";
import { EmptyState } from "../empty";
import Pagination from "../pagination";

export const TableComponent = ({
  headers = [],
  rows = [],
  className = "",
  isRowClickable = false,
  rowClassName = "",
  onRowClick = () => {},
  isTableEmpty = false,
  isLoading = false,
  emptyTitle = "Empty",
  emptyImage,
  emptySubTitle = "No data found",
  loadingRows = 5,
  loadingHeight = 42,
  ctaText = "",
  onCTAClick = () => {},
  ctaIcon = "",
  isPaginated = false,
  currentPage,
  totalCount,
  itemsPerPage,
  pageRangeDisplayed,
  onPageChange,
}: {
  headers: ReactNode[];
  rows: {
    id: string | number;
    items: ReactNode[];
  }[];
  className?: string;
  isRowClickable?: boolean;
  rowClassName?: string;
  onRowClick?: (clickedIndex: number) => void;
  isTableEmpty?: boolean;
  isLoading?: boolean;
  emptyTitle?: string;
  emptySubTitle?: string;
  emptyImage?: string;
  loadingRows?: number;
  loadingHeight?: number;
  ctaText?: string;
  onCTAClick?: () => void;
  ctaIcon?: string;
  isPaginated?: boolean;
  currentPage?: number;
  totalCount?: number;
  itemsPerPage?: number;
  pageRangeDisplayed?: number;
  onPageChange?: (page: number) => void;
}) => {
  return (
    <div>
      <table
        className={classNames(
          `min-w-full !overflow-x-scroll text-sm md:min-w-full`,
          "border-separate border-spacing-x-0 border-spacing-y-0",
          "rounded-2xl border border-grey-200",
          className,
        )}
      >
        {isLoading ? (
          <tbody>
            <tr>
              <td className="p-3">
                <Skeleton
                  count={loadingRows}
                  height={loadingHeight}
                  baseColor={SKELETON_BASE_COLOR}
                  highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  className="my-1"
                />
              </td>
            </tr>
          </tbody>
        ) : undefined}

        {isTableEmpty && !isLoading ? (
          <tbody>
            <tr>
              <td>
                <EmptyState
                  text={emptyTitle}
                  subtext={emptySubTitle}
                  textClassName="!text-[24px] !mt-2"
                  subtextClassName="!max-w-[330px]"
                  className="!gap-2"
                  ctaText={ctaText}
                  onCTAClick={onCTAClick}
                  ctaIcon={ctaIcon}
                  image={emptyImage}
                />
              </td>
            </tr>
          </tbody>
        ) : undefined}

        {!isTableEmpty && !isLoading ? (
          <>
            <thead>
              <tr>
                {headers?.map((header, idx) => {
                  const key = `table-header-${idx}`;
                  return header ? (
                    <th
                      key={key}
                      className="min-w-max border-b border-grey-200 p-3 text-left"
                    >
                      {header}
                    </th>
                  ) : undefined;
                })}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, idx) => {
                return (
                  <tr
                    key={row?.id}
                    className={`group border-spacing-y-80${
                      isRowClickable &&
                      "transition-all duration-300 hover:cursor-pointer hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      isRowClickable && onRowClick(idx);
                    }}
                  >
                    {row?.items?.map((item, idx) => {
                      const key = `row-item-${idx}`;
                      return item ? (
                        <td
                          key={key}
                          className={classNames(
                            `border-b border-grey-200 p-3 text-left transition-all duration-300`,
                            "group-last:border-b-0 group-last:first:rounded-bl-2xl group-last:last:rounded-br-2xl",
                            headers.length === 0 &&
                              "group-first:first:rounded-tl-2xl group-first:last:rounded-tr-2xl",
                            rowClassName,
                          )}
                        >
                          {item}
                        </td>
                      ) : undefined;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </>
        ) : undefined}
      </table>

      {isPaginated && !isTableEmpty && (
        <Pagination
          currentPage={currentPage!}
          totalCount={totalCount!}
          itemsPerPage={itemsPerPage!}
          onPageChange={onPageChange!}
          pageRangeDisplayed={pageRangeDisplayed}
          containerClassName="mt-3"
          showPagNumbers={false}
        />
      )}
    </div>
  );
};
